/**
 * Getting a recording into a format the SpeedRadio app can actually play.
 *
 * The platform accepts aac, m4a, mp3 and wav — and defaults to aac. Browsers,
 * meanwhile, only record what they feel like: Safari gives MP4/AAC, Chrome
 * usually gives WebM/Opus, which the app cannot play at all. So the take is
 * normalised here, before upload, rather than discovering the problem after
 * a reviewer has approved it.
 *
 * Three routes, best first:
 *
 * 1. **Record straight to AAC.** When MediaRecorder supports `audio/mp4` the
 *    output is already AAC in an MP4 container. Nothing to convert — no
 *    quality loss, no encode wait.
 * 2. **Transcode to AAC with WebCodecs.** Decode the take, re-encode with
 *    AAC-LC, and frame it as ADTS so it is a playable `.aac` file.
 * 3. **Fall back to MP3.** A pure-JS encoder that works everywhere. Still on
 *    the platform's accepted list, so a contributor is never blocked by an
 *    old browser.
 */
import { Mp3Encoder } from "@breezystack/lamejs";

export interface PreparedAudio {
  blob: Blob;
  /** What to send as the file's content type. */
  contentType: string;
  extension: string;
  durationSeconds: number;
  /** Which of the three routes produced this, for logging and support. */
  route: "native-aac" | "webcodecs-aac" | "mp3";
}

const TARGET_BITRATE = 64_000; // voice notes; roughly WhatsApp quality
const AAC_CODEC = "mp4a.40.2"; // AAC-LC

/**
 * The recording format to ask MediaRecorder for.
 *
 * `audio/mp4` first: that is AAC already, so there is nothing to transcode.
 */
export function pickRecordingMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const preferred = [
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/aac",
    "audio/webm;codecs=opus",
    "audio/webm",
  ];
  return preferred.find((type) => MediaRecorder.isTypeSupported(type));
}

function isAacContainer(mimeType: string): boolean {
  const type = (mimeType || "").toLowerCase();
  return type.includes("audio/mp4") || type.includes("audio/aac") || type.includes("m4a");
}

async function decode(source: Blob): Promise<AudioBuffer> {
  const arrayBuffer = await source.arrayBuffer();
  const AudioCtx =
    window.AudioContext ||
    (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const context = new AudioCtx();
  try {
    return await context.decodeAudioData(arrayBuffer);
  } finally {
    // decodeAudioData detaches the buffer; close either way so takes do not
    // pile up audio contexts across re-records.
    if (context.state !== "closed") void context.close();
  }
}

/** Average to mono — a phone mic often reports stereo, and voice gains nothing from it. */
function toMono(buffer: AudioBuffer): Float32Array {
  if (buffer.numberOfChannels === 1) return buffer.getChannelData(0);
  const left = buffer.getChannelData(0);
  const right = buffer.getChannelData(1);
  const mono = new Float32Array(left.length);
  for (let i = 0; i < left.length; i += 1) mono[i] = (left[i] + right[i]) / 2;
  return mono;
}

/* -------------------------------------------------------------- ADTS ---- */

// Sample rates an AAC stream can declare, by index.
const ADTS_SAMPLE_RATES = [
  96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350,
];

/**
 * Wrap a raw AAC frame in an ADTS header.
 *
 * Encoders hand back bare AAC frames, which no player can open on their own —
 * ADTS is the 7-byte-per-frame header that makes the stream a `.aac` file.
 * (Chrome can emit ADTS itself; this covers the encoders that do not.)
 */
function adtsHeader(frameLength: number, sampleRate: number, channels: number): Uint8Array {
  const rateIndex = ADTS_SAMPLE_RATES.indexOf(sampleRate);
  if (rateIndex < 0) throw new Error(`AAC cannot declare a ${sampleRate}Hz stream`);

  const fullLength = frameLength + 7;
  const header = new Uint8Array(7);
  header[0] = 0xff;
  // 0xf1: MPEG-4, layer 0, no CRC.
  header[1] = 0xf1;
  // profile (AAC-LC = 1, stored as profile-1), sample rate index, channel config high bit
  header[2] = ((1 << 6) | (rateIndex << 2) | ((channels >> 2) & 0x01)) & 0xff;
  header[3] = (((channels & 0x03) << 6) | ((fullLength >> 11) & 0x03)) & 0xff;
  header[4] = (fullLength >> 3) & 0xff;
  header[5] = (((fullLength & 0x07) << 5) | 0x1f) & 0xff;
  header[6] = 0xfc;
  return header;
}

function alreadyAdts(chunk: Uint8Array): boolean {
  return chunk.length > 1 && chunk[0] === 0xff && (chunk[1] & 0xf0) === 0xf0;
}

type AudioEncoderCtor = typeof globalThis extends { AudioEncoder: infer T } ? T : never;

function audioEncoder(): AudioEncoderCtor | undefined {
  return (window as unknown as { AudioEncoder?: AudioEncoderCtor }).AudioEncoder;
}

export function canEncodeAac(): boolean {
  return (
    typeof audioEncoder() !== "undefined" &&
    typeof (window as unknown as { AudioData?: unknown }).AudioData !== "undefined"
  );
}

/**
 * Re-encode a decoded take as AAC-LC, framed as ADTS.
 *
 * Throws if WebCodecs is unavailable or refuses the configuration; the caller
 * falls back to MP3.
 */
async function encodeToAac(buffer: AudioBuffer): Promise<Blob> {
  const Encoder = audioEncoder() as unknown as {
    new (init: { output: (chunk: unknown, meta: unknown) => void; error: (e: Error) => void }): {
      configure: (config: unknown) => void;
      encode: (data: unknown) => void;
      flush: () => Promise<void>;
      close: () => void;
      state: string;
    };
    isConfigSupported?: (config: unknown) => Promise<{ supported?: boolean }>;
  };
  if (!Encoder) throw new Error("WebCodecs AudioEncoder is unavailable");

  const sampleRate = buffer.sampleRate;
  if (ADTS_SAMPLE_RATES.indexOf(sampleRate) < 0) {
    // Rather than resample by hand, let the MP3 path take it.
    throw new Error(`Unsupported sample rate for AAC: ${sampleRate}`);
  }

  const config = {
    codec: AAC_CODEC,
    sampleRate,
    numberOfChannels: 1,
    bitrate: TARGET_BITRATE,
    // Chrome honours this and emits ADTS directly; other engines ignore it
    // and we frame the output ourselves below.
    aac: { format: "adts" },
  };

  if (Encoder.isConfigSupported) {
    const support = await Encoder.isConfigSupported(config);
    if (!support?.supported) throw new Error("AAC encoding is not supported here");
  }

  const mono = toMono(buffer);
  const parts: Uint8Array[] = [];
  let failure: Error | null = null;

  const encoder = new Encoder({
    output: (chunk: unknown) => {
      const encoded = chunk as { byteLength: number; copyTo: (target: Uint8Array) => void };
      const bytes = new Uint8Array(encoded.byteLength);
      encoded.copyTo(bytes);
      parts.push(alreadyAdts(bytes) ? bytes : concat(adtsHeader(bytes.length, sampleRate, 1), bytes));
    },
    error: (err: Error) => {
      failure = err;
    },
  });
  encoder.configure(config);

  const AudioDataCtor = (window as unknown as { AudioData: new (init: unknown) => unknown })
    .AudioData;

  // AAC works a frame at a time; 1024 samples is the AAC-LC frame size.
  const FRAME = 1024;
  let timestamp = 0;
  for (let offset = 0; offset < mono.length; offset += FRAME) {
    const slice = mono.subarray(offset, Math.min(offset + FRAME, mono.length));
    // The final partial frame must still be full length or the encoder drops it.
    const padded = slice.length === FRAME ? slice : (() => {
      const full = new Float32Array(FRAME);
      full.set(slice);
      return full;
    })();
    encoder.encode(
      new AudioDataCtor({
        format: "f32-planar",
        sampleRate,
        numberOfFrames: FRAME,
        numberOfChannels: 1,
        timestamp,
        data: padded,
      }),
    );
    timestamp += Math.round((FRAME / sampleRate) * 1_000_000);
  }

  await encoder.flush();
  encoder.close();

  if (failure) throw failure;
  if (parts.length === 0) throw new Error("AAC encoder produced nothing");

  return new Blob(parts as BlobPart[], { type: "audio/aac" });
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

/* --------------------------------------------------------------- MP3 ---- */

function floatTo16BitPcm(input: Float32Array): Int16Array {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, input[i]));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
}

function encodeToMp3(buffer: AudioBuffer): Blob {
  const samples = floatTo16BitPcm(toMono(buffer));
  const encoder = new Mp3Encoder(1, buffer.sampleRate, TARGET_BITRATE / 1000);
  // lamejs types its output as Int8Array but returns a Uint8Array; either is
  // fine as a BlobPart.
  const chunks: Uint8Array[] = [];
  const BLOCK = 1152;
  for (let offset = 0; offset < samples.length; offset += BLOCK) {
    const frame = encoder.encodeBuffer(samples.subarray(offset, offset + BLOCK));
    if (frame.length > 0) chunks.push(frame as unknown as Uint8Array);
  }
  const tail = encoder.flush();
  if (tail.length > 0) chunks.push(tail as unknown as Uint8Array);
  return new Blob(chunks as BlobPart[], { type: "audio/mpeg" });
}

/* ----------------------------------------------------------- entry point */

/**
 * Cut a recording back to the limit.
 *
 * "Stopped the recorder at 60 seconds" and "60 seconds of audio" are not the
 * same thing. The timer only fires every 100ms, and the recorder flushes
 * whatever slice is in flight when it stops, so the decoded audio can run a
 * fraction over. The API measures the audio rather than the clock, so a
 * full-length take came back as "must be under 60 seconds" — which is a
 * miserable way to lose a recording somebody has just made.
 */
export function trimTo(buffer: AudioBuffer, maxSeconds: number): AudioBuffer {
  if (!Number.isFinite(maxSeconds) || maxSeconds <= 0) return buffer;
  const maxLength = Math.floor(maxSeconds * buffer.sampleRate);
  if (buffer.length <= maxLength) return buffer;

  const trimmed = new AudioBuffer({
    length: maxLength,
    numberOfChannels: buffer.numberOfChannels,
    sampleRate: buffer.sampleRate,
  });
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    trimmed.copyToChannel(buffer.getChannelData(channel).subarray(0, maxLength), channel);
  }
  return trimmed;
}

/**
 * Turn whatever MediaRecorder produced into something the platform can play.
 *
 * `maxSeconds` is the platform's own limit. Anything longer is trimmed here
 * rather than refused later.
 */
export async function prepareForUpload(
  recorded: Blob,
  maxSeconds = Infinity,
): Promise<PreparedAudio> {
  // Already AAC: leave it alone, unless it runs over the limit — then it has
  // to be trimmed, which means re-encoding it after all.
  if (isAacContainer(recorded.type)) {
    const buffer = await decode(recorded).catch(() => null);
    if (!buffer || buffer.duration <= maxSeconds) {
      return {
        blob: recorded,
        contentType: "audio/mp4",
        extension: "m4a",
        durationSeconds: buffer?.duration ?? 0,
        route: "native-aac",
      };
    }
  }

  const buffer = trimTo(await decode(recorded), maxSeconds);

  if (canEncodeAac()) {
    try {
      return {
        blob: await encodeToAac(buffer),
        contentType: "audio/aac",
        extension: "aac",
        durationSeconds: buffer.duration,
        route: "webcodecs-aac",
      };
    } catch (err) {
      // Not fatal: MP3 is on the platform's accepted list too. Worth a log,
      // because a browser silently falling back is worth knowing about.
      console.warn("AAC encoding unavailable, falling back to MP3:", err);
    }
  }

  return {
    blob: encodeToMp3(buffer),
    contentType: "audio/mpeg",
    extension: "mp3",
    durationSeconds: buffer.duration,
    route: "mp3",
  };
}
